import type { BoardMeta, BoardNode, CallChatMessage, Presence } from '@kanvas/board-core';
import { useCallback, useMemo, useSyncExternalStore } from 'react';
import type { BoardDoc, ConnectionState } from './BoardDoc';

/**
 * React bindings for the board document.
 *
 * The pattern throughout is: subscribe to a monotonic version number, then
 * derive values with `useMemo`. Subscribing to derived objects directly would
 * fight `useSyncExternalStore`, which requires a snapshot that is referentially
 * stable between changes — and `getMeta()` builds a fresh object every call.
 */
export function useDocVersion(doc: BoardDoc): number {
  const subscribe = useCallback((listener: () => void) => doc.subscribe(listener), [doc]);
  return useSyncExternalStore(subscribe, doc.getVersion, doc.getVersion);
}

export function usePresenceVersion(doc: BoardDoc): number {
  const subscribe = useCallback(
    (listener: () => void) => doc.subscribePresence(listener),
    [doc],
  );
  return useSyncExternalStore(subscribe, doc.getPresenceVersion, doc.getPresenceVersion);
}

export function useRosterVersion(doc: BoardDoc): number {
  const subscribe = useCallback(
    (listener: () => void) => doc.subscribeRoster(listener),
    [doc],
  );
  return useSyncExternalStore(subscribe, doc.getRosterVersion, doc.getRosterVersion);
}

/** Nodes in paint order. The array identity only changes when the document does. */
export function useNodes(doc: BoardDoc): BoardNode[] {
  const version = useDocVersion(doc);
  return useMemo(() => doc.all(), [doc, version]);
}

/** Whether the board has zero nodes — avoids sorting for empty-state chrome. */
export function useIsEmpty(doc: BoardDoc): boolean {
  const version = useDocVersion(doc);
  return useMemo(() => doc.isEmpty(), [doc, version]);
}

export function useNode(doc: BoardDoc, id: string | null): BoardNode | undefined {
  const version = useDocVersion(doc);
  return useMemo(() => (id ? doc.get(id) : undefined), [doc, id, version]);
}

export function useBoardMeta(doc: BoardDoc): BoardMeta {
  const version = useDocVersion(doc);
  return useMemo(() => doc.getMeta(), [doc, version]);
}

/**
 * Online peers for the avatar stack / follow banner.
 *
 * Subscribes to roster changes only — cursor and viewport packets must not
 * rebuild this list on every tick.
 */
export function usePeers(doc: BoardDoc): Presence[] {
  const version = useRosterVersion(doc);
  return useMemo(() => doc.getPresence(), [doc, version]);
}

export function useConnection(doc: BoardDoc): ConnectionState {
  const subscribe = useCallback(
    (listener: () => void) => doc.onConnectionChange(() => listener()),
    [doc],
  );
  const getSnapshot = useCallback(() => doc.getConnection(), [doc]);
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

export function useTimer(doc: BoardDoc): ReturnType<BoardDoc['getTimer']> {
  const version = useDocVersion(doc);
  return useMemo(() => doc.getTimer(), [doc, version]);
}

export function useVoteSession(doc: BoardDoc): ReturnType<BoardDoc['getVoteSession']> {
  const version = useDocVersion(doc);
  return useMemo(() => doc.getVoteSession(), [doc, version]);
}

export function useVoteTotals(doc: BoardDoc): Map<string, number> {
  const version = useDocVersion(doc);
  return useMemo(() => doc.voteTotals(), [doc, version]);
}

export function useMyVotes(doc: BoardDoc, userId: string): Record<string, number> {
  const version = useDocVersion(doc);
  return useMemo(() => doc.getVotesFor(userId), [doc, userId, version]);
}

/** In-call chat, narrowed to one call. Empty until a call is joined. */
export function useCallChat(doc: BoardDoc, callId: string | null): CallChatMessage[] {
  const version = useDocVersion(doc);
  return useMemo(() => (callId ? doc.getCallChat(callId) : []), [doc, callId, version]);
}

/** Whether undo/redo are available, so the buttons can disable themselves. */
export function useUndoState(doc: BoardDoc): { canUndo: boolean; canRedo: boolean } {
  const version = useDocVersion(doc);
  return useMemo(
    () => ({
      canUndo: doc.undoManager.undoStack.length > 0,
      canRedo: doc.undoManager.redoStack.length > 0,
    }),
    [doc, version],
  );
}
